<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useTaxesApi } from '@/composables/useTaxesApi.ts';
import { formatCurrencyWithoutDecimal } from '@/helpers/currencyHelper.ts';
import type { LocaleSchema } from '@/locales/index.ts';
import type { NewQuoteInfo } from '@/typings/quotes/NewQuotesInfo.ts';
import type { CalculateHousingPurchaseTaxesResult } from '@/typings/taxes/CalculateHousingPurchaseTaxesResult.ts';

const { t, locale } = useI18n<{message: LocaleSchema}>({
  useScope: 'global',
});

const { calculateHousingPurchaseTaxes } = useTaxesApi();

const calculatedPrice = defineModel<number>();
const taxCalculation = ref<CalculateHousingPurchaseTaxesResult | undefined>();

const props = defineProps<{
  quoteInfo: NewQuoteInfo;
  selectedConfigurationId?: number;
  selectedOptions?: number[];
  isFirstTimeHomebuyer?: boolean;
}>();

const basePrice = computed(() => {
  // Si le prix de base est masqué (null), retourner null
  if (props.quoteInfo.unit.basePrice === null) {
    return null;
  }
  
  let price = props.quoteInfo.unit.basePrice;
  
  
  if (props.quoteInfo.unit.hasDefaultOptions) {
    price += props.quoteInfo.options
      .filter(option => option.quantity > 0)
      .reduce((total, option) => total + option.price, 0);
  }
  
  return price;
});

const basePriceWithConfig = computed(() => {
  if (basePrice.value === null) return null;
  return basePrice.value + unitConfigPrice.value;
});

const selectedOptionsPrice = computed(() => {
  const price = props.quoteInfo.options
    .filter(option => props.selectedOptions!.includes(option.optionId))
    .reduce((total, option) => total + option.price, 0);
  return price;
});

const subtotalPrice = computed(() => {
  if (basePriceWithConfig.value === null) return null;
  return basePriceWithConfig.value + selectedOptionsPrice.value;
});

const basePriceWithSelectedOptions = computed(() => {
  if (basePriceWithConfig.value === null) return null;
  return basePriceWithConfig.value + selectedOptionsPrice.value;
});

const taxes = computed(() => {
  return taxCalculation.value?.taxes;
});

const totalAmount = computed(() => {
  if (basePriceWithConfig.value === null || !taxCalculation.value) return null;
  if (!props.isFirstTimeHomebuyer) return taxCalculation.value.totalPrice;
  return taxCalculation.value.totalPrice - taxCalculation.value.taxes.tps.netTaxAmount;
});

const formatedUnitConfigPrice = computed(() => unitConfigPrice.value > 0 ? formatCurrencyWithoutDecimal(unitConfigPrice.value, locale) : null);
const formattedBasePrice = computed(() => basePrice.value !== null ? formatCurrencyWithoutDecimal(basePrice.value, locale) : t('common.notAvailableAbbreviated'));
const formattedSelectedOptionsPrice = computed(() => selectedOptionsPrice.value !== null ? formatCurrencyWithoutDecimal(selectedOptionsPrice.value, locale) : t('common.notAvailableAbbreviated'));
const formattedsubtotalPrice = computed(() => subtotalPrice.value !== null ? formatCurrencyWithoutDecimal(subtotalPrice.value, locale) : t('common.notAvailableAbbreviated'));
const formattedTPS = computed(() => basePriceWithConfig.value !== null && taxes.value ? formatCurrencyWithoutDecimal(taxes.value.tps.taxAmount || 0, locale) : t('common.notAvailableAbbreviated'));
const formattedTPSRebate = computed(() => basePriceWithConfig.value !== null && taxes.value ? formatCurrencyWithoutDecimal(props.isFirstTimeHomebuyer ? -(taxes.value.tps.taxAmount || 0) : -(taxes.value.tps.refundAmount || 0), locale) : t('common.notAvailableAbbreviated'));
const formattedTVQ = computed(() => basePriceWithConfig.value !== null && taxes.value ? formatCurrencyWithoutDecimal(taxes.value.tvq.taxAmount || 0, locale) : t('common.notAvailableAbbreviated'));
const formattedTVQRebate = computed(() => basePriceWithConfig.value !== null && taxes.value ? formatCurrencyWithoutDecimal(-(taxes.value.tvq.refundAmount || 0), locale) : t('common.notAvailableAbbreviated'));
const formattedTotal = computed(() => totalAmount.value !== null ? formatCurrencyWithoutDecimal(totalAmount.value, locale) : t('common.notAvailableAbbreviated'));

const unitPriceLabel = computed(() => {
  return props.quoteInfo.unit.hasDefaultOptions ?  t('unitCostDetails.unitPrice') : t('unitCostDetails.unitBaseCost');
});

const unitConfigPrice = computed(() => {
  if (props.selectedConfigurationId && props.quoteInfo.unit.unitUnitConfigurations) {
    const configImage = props.quoteInfo.unit.unitUnitConfigurations.find(
      ci => ci.unitConfigurationId === props.selectedConfigurationId
    );
    return configImage?.amount || 0;
  }
  return 0;
});

watch(basePriceWithSelectedOptions, async (newPrice) => {
  if (newPrice !== null && newPrice > 0) {
    const result = await calculateHousingPurchaseTaxes(newPrice);
    if (result) {
      taxCalculation.value = result;
      calculatedPrice.value = totalAmount.value ?? 0;
    }
  } else {
    taxCalculation.value = undefined;
    calculatedPrice.value = 0;
  }
}, { immediate: true });

watch(totalAmount, (newTotal) => {
  calculatedPrice.value = newTotal ?? 0;
});
</script>

<template>
  <div v-if="basePrice === null" class="unitCostDetails-hiddenPrice">
    <p>{{ t('unitCostDetails.priceNotAvailable') }}</p>
  </div>
  <table v-else class="unitCostDetails">
    <tbody>
      <tr v-if="unitConfigPrice > 0">
        <th>{{ t('unitCostDetails.unitConfigPrice') }}</th>
        <td>{{ formatedUnitConfigPrice }}</td>
      </tr>
      <tr>
        <th>{{ unitPriceLabel }}</th>
        <td>{{ formattedBasePrice }}</td>
      </tr>
      <tr v-if="selectedOptionsPrice > 0">
        <th>{{ t('unitCostDetails.selectedOptionsPrice') }}</th>
        <td>{{ formattedSelectedOptionsPrice }}</td>
      </tr>
      <tr v-if="selectedOptionsPrice > 0">
        <th>{{ t('unitCostDetails.subtotal') }}</th>
        <td>{{ formattedsubtotalPrice }}</td>
      </tr>
      <tr>
        <th>{{ t('unitCostDetails.gst') }}</th>
        <td>{{ formattedTPS }}</td>
      </tr>
      <tr v-if="taxes">
        <th>{{ t('unitCostDetails.gstRebate') }}</th>
        <td>{{ formattedTPSRebate }}</td>
      </tr>
      <tr>
        <th>{{ t('unitCostDetails.qst') }}</th>
        <td>{{ formattedTVQ }}</td>
      </tr>
      <tr v-if="taxes">
        <th>{{ t('unitCostDetails.qstRebate') }}</th>
        <td>{{ formattedTVQRebate }}</td>
      </tr>
      <tr class="unitCostDetails-total">
        <th>{{ t('unitCostDetails.total') }}</th>
        <td>{{ formattedTotal }}</td>
      </tr>
    </tbody>
  </table>
</template>

<style lang="scss">
.unitCostDetails {
  width: 100%;
  border-collapse: collapse;
  margin-top: 12px;
  
  th, td {
    padding: 6px 8px;
    text-align: left;
  }
  
  th {
    font-weight: 600;
  }
  
  td {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
  
  tr:nth-child(even) {
    background-color: var(--gray-100);
  }
  
  &-total {
    font-weight: 700;
    
    th, td {
      padding-top: 12px;
      border-top: 1px solid var(--gray-300);
    }
  }
}
</style>

